import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Calendar, MapPin, Users, Edit, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import EventLocationMap from './EventLocationMap';
import EditEventDialog from './EditEventDialog';

interface Event {
  id: string;
  title: string;
  location: string;
  date: string;
  time: string;
  description: string;
  max_attendees: number;
  location_image?: string;
}

interface EventCardProps {
  event: Event;
  onEventUpdated?: () => void;
}

const EventCard = ({ event, onEventUpdated }: EventCardProps) => {
  const [showSignup, setShowSignup] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [registration, setRegistration] = useState({
    name: '',
    email: '',
    attendee_count: 1
  });
  const { toast } = useToast();
  const { isAdmin } = useAuth();

  // Check if event date is in the past
  const isEventPast = () => {
    const eventDate = new Date(`${event.date}T${event.time || '00:00'}`);
    const now = new Date();
    return eventDate < now;
  };

  // Don't render if event is in the past
  if (isEventPast()) {
    return null;
  }

  const handleSignup = async () => {
    try {
      const { error } = await supabase
        .from('registrations')
        .insert([{
          event_id: event.id,
          ...registration
        }]);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Registration successful! We'll see you there."
      });
      
      setRegistration({ name: '', email: '', attendee_count: 1 });
      setShowSignup(false);
    } catch (error) {
      console.error('Error registering:', error);
      toast({
        title: "Error",
        description: "Failed to register. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', event.id);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Event deleted successfully"
      });
      
      if (onEventUpdated) onEventUpdated();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({
        title: "Error",
        description: "Failed to delete event",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr: string) => {
    return new Date(`2000-01-01T${timeStr}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      {event.location_image && (
        <div className="w-full h-48 overflow-hidden rounded-t-lg">
          <img
            src={event.location_image}
            alt={`${event.location} venue`}
            className="w-full h-full object-cover"
            onError={(e) => {
              console.error('Failed to load image:', event.location_image);
              console.error('Image error details:', e);
              // Try to reload the image without cache busting parameter
              const target = e.target as HTMLImageElement;
              if (target.src.includes('?t=')) {
                const baseUrl = target.src.split('?t=')[0];
                target.src = baseUrl + '?reload=' + Date.now();
              } else {
                // Hide the image container if it still fails to load
                const container = target.parentElement;
                if (container) {
                  container.style.display = 'none';
                }
              }
            }}
            onLoad={() => {
              console.log('Image loaded successfully:', event.location_image);
            }}
          />
        </div>
      )}
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="text-lg">{event.title}</CardTitle>
            <CardDescription>{event.description}</CardDescription>
          </div>
          {isAdmin && (
            <div className="flex gap-1 ml-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEdit(true)}
                className="h-8 w-8 p-0"
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4" />
              <span>{event.location}</span>
            </div>
            <EventLocationMap location={event.location} eventTitle={event.title} />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(event.date)} at {formatTime(event.time)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Users className="w-4 h-4" />
            <span>Max {event.max_attendees} attendees</span>
          </div>
        </div>
        
        <Dialog open={showSignup} onOpenChange={setShowSignup}>
          <DialogTrigger asChild>
            <Button className="w-full mt-4">
              Sign Up
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Register for {event.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {event.location_image && (
                <div className="w-full h-48 rounded-lg overflow-hidden">
                  <img
                    src={event.location_image}
                    alt={`${event.location} venue`}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={registration.name}
                  onChange={(e) => setRegistration({...registration, name: e.target.value})}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={registration.email}
                  onChange={(e) => setRegistration({...registration, email: e.target.value})}
                  placeholder="john@example.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="attendee_count">Number of Attendees</Label>
                <Input
                  id="attendee_count"
                  type="number"
                  min="1"
                  max="10"
                  value={registration.attendee_count}
                  onChange={(e) => setRegistration({...registration, attendee_count: parseInt(e.target.value) || 1})}
                />
              </div>
              <Button 
                onClick={handleSignup} 
                className="w-full"
                disabled={!registration.name || !registration.email}
              >
                Complete Registration
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
      
      {isAdmin && (
        <EditEventDialog
          event={event}
          open={showEdit}
          onOpenChange={setShowEdit}
          onEventUpdated={onEventUpdated || (() => {})}
        />
      )}
    </Card>
  );
};

export default EventCard;